import {
  MessagingChannel_LastMessage_Read,
  MessagingChannel_Read,
  MessagingChannel_Recipient_Read,
  MessagingChannel_UnreadCounts_Read,
  MessagingChannelMember_Read,
  MessagingChannelMemberRole,
  MessagingChannelReplyMode,
  MessagingChannelTargetType,
  MessagingChannelType,
  MessagingMessage_Read,
  MessagingMessage_RepliedToBroadcastChannel_Read,
  Principal_Read_Nested,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

import { FileAttachmentBasic } from '~hooks/useFileAttachment/models';
import { Nullable } from '~types/Nullable';

type MessagingChannelTarget = NonNullable<MessagingChannel_Read['target']>;

export type MessagingChannelTargetReference = {
  type: MessagingChannelTargetType;
  id: string;
};

/** A target that is not a job or order reference is a full nested principal. */
export const isMessagingChannelTargetReference = (
  target: MessagingChannelTarget,
): target is MessagingChannelTargetReference =>
  target.type === MessagingChannelTargetType.JOB ||
  target.type === MessagingChannelTargetType.ORDER;

// `Principal_Read_Nested` cannot be narrowed by `type`, so fields are read structurally.
type PrincipalFields = {
  id?: string;
  first_name?: string;
  last_name?: string;
  legal_name?: string;
  impersonated?: { first_name?: string; last_name?: string };
  company?: { id?: string; legal_name?: string };
};

/** Null for the one principal shape that carries no id of its own, an impersonation. */
export const principalId = (principal: Principal_Read_Nested): Nullable<string> =>
  (principal as PrincipalFields).id ?? null;

const principalCompany = (principal: Principal_Read_Nested) =>
  (principal as PrincipalFields).company;

const joinName = (first?: string, last?: string) =>
  [first, last].filter(Boolean).join(' ').trim();

/** Probe order matters: a principal with both is named as the person. */
export const principalDisplayName = (
  principal: Principal_Read_Nested,
): Nullable<string> => {
  const fields = principal as PrincipalFields;

  const personName = joinName(fields.first_name, fields.last_name);
  if (personName) {
    return personName;
  }

  if (fields.legal_name) {
    return fields.legal_name;
  }

  const impersonatedName = joinName(
    fields.impersonated?.first_name,
    fields.impersonated?.last_name,
  );
  if (impersonatedName) {
    return impersonatedName;
  }

  return fields.company?.legal_name ?? null;
};

export type MessagingTyper = { id: string; name: string };

export const principalFirstName = (
  principal: Principal_Read_Nested,
): Nullable<string> => {
  const fields = principal as PrincipalFields;

  return (
    fields.first_name ||
    fields.impersonated?.first_name ||
    principalDisplayName(principal)
  );
};

export class MessagingChannelMember {
  public static parse(proto: MessagingChannelMember_Read) {
    return new MessagingChannelMember(
      proto.id,
      proto.principal,
      proto.role,
      proto.last_read_message_id ?? null,
      proto.last_read_message_created_at
        ? dayjs(proto.last_read_message_created_at)
        : null,
    );
  }

  public get id() {
    return this._id;
  }

  public get principal() {
    return this._principal;
  }

  public get role() {
    return this._role;
  }

  public get displayName() {
    return principalDisplayName(this._principal);
  }

  public get principalId() {
    return principalId(this._principal);
  }

  public get companyId() {
    return principalCompany(this._principal)?.id ?? null;
  }

  public get companyName() {
    return principalCompany(this._principal)?.legal_name ?? null;
  }

  /** The api orders read position by `(created_at, id)` but sends milliseconds, so a tie on the timestamp falls to the id. */
  public hasRead(message: MessagingMessage) {
    const readMessageId = this._last_read_message_id;
    const readMessageCreatedAt = this._last_read_message_created_at;
    if (!readMessageId || !readMessageCreatedAt) return false;

    if (message.createdAt.isSame(readMessageCreatedAt)) {
      return message.id <= readMessageId;
    }

    return message.createdAt.isBefore(readMessageCreatedAt);
  }

  constructor(
    private _id: string,
    private _principal: Principal_Read_Nested,
    private _role: MessagingChannelMemberRole,
    private _last_read_message_id: Nullable<string>,
    private _last_read_message_created_at: Nullable<Dayjs>,
  ) {}
}

export class MessagingChannelLastMessage {
  public static parse(proto: MessagingChannel_LastMessage_Read) {
    return new MessagingChannelLastMessage(
      proto.id,
      proto.sender,
      proto.excerpt,
      proto.file_attachment_count,
      proto.deleted_at ? dayjs(proto.deleted_at) : null,
      dayjs(proto.created_at),
      proto.removed_by_moderator,
    );
  }

  /** A preview row ellipsizes to one line, so the api's length cap needs no copy here. */
  public static fromMessage(message: MessagingMessage) {
    const firstLine = message.content?.split(/\r?\n/)[0]?.trim();

    return new MessagingChannelLastMessage(
      message.id,
      message.sender,
      message.isDeleted ? null : firstLine || null,
      message.isDeleted ? 0 : message.fileAttachments.length,
      message.deletedAt ?? null,
      message.createdAt,
      message.removedByModerator,
    );
  }

  public get id() {
    return this._id;
  }

  public get sender() {
    return this._sender;
  }

  public get senderId() {
    return principalId(this._sender);
  }

  /** Null for a message with attachments only, and for a deleted one. */
  public get excerpt() {
    return this._excerpt;
  }

  public get fileAttachmentCount() {
    return this._file_attachment_count;
  }

  public get isDeleted() {
    return this._deleted_at !== null;
  }

  public get isRemovedByModerator() {
    return this.isDeleted && this._removed_by_moderator;
  }

  public get createdAt() {
    return this._created_at;
  }

  constructor(
    private _id: string,
    private _sender: Principal_Read_Nested,
    private _excerpt: string | null,
    private _file_attachment_count: number,
    private _deleted_at: Dayjs | null,
    private _created_at: Dayjs,
    private _removed_by_moderator = false,
  ) {}
}

export class MessagingChannel {
  public static parse(proto: MessagingChannel_Read) {
    return new MessagingChannel(
      proto.id,
      proto.company_id,
      proto.channel_type,
      proto.reply_mode,
      proto.name,
      proto.organizer,
      proto.members.map(MessagingChannelMember.parse),
      proto.target,
      proto.archived_at ? dayjs(proto.archived_at) : null,
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      dayjs(proto.last_message_at),
      proto.last_message ? MessagingChannelLastMessage.parse(proto.last_message) : null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.unread_count,
      proto.muted,
    );
  }

  public get id() {
    return this._id;
  }

  public get companyId() {
    return this._company_id;
  }

  public get channelType() {
    return this._channel_type;
  }

  public get replyMode() {
    return this._reply_mode;
  }

  public get name() {
    return this._name;
  }

  public get organizer() {
    return this._organizer;
  }

  public get organizerName() {
    return principalDisplayName(this._organizer);
  }

  public get members() {
    return this._members;
  }

  /** The organizer is a member too, so the recipients are everyone but the owner. */
  public get recipients() {
    return this._members.filter(
      (member) => member.role !== MessagingChannelMemberRole.OWNER,
    );
  }

  public get recipientCount() {
    return this.recipients.length;
  }

  /** The api counts reads from current members only, so the total is the current members but the sender. */
  public readerCandidateCountFor(message: MessagingMessage) {
    const senderId = principalId(message.sender);

    return this._members.filter((member) => member.principalId !== senderId).length;
  }

  /** Named for the row that shows a counterpart instead of a count. */
  public get soleRecipientName() {
    const { recipients } = this;

    return recipients.length === 1 ? recipients[0].displayName : null;
  }

  /**
   * The person the viewer is talking to: the sole recipient unless that is the viewer, else
   * the only other member.
   */
  public counterpartMember(viewerUserId: Nullable<string>) {
    const [soleRecipient] = this.recipientCount === 1 ? this.recipients : [];
    if (soleRecipient?.principalId && soleRecipient.principalId !== viewerUserId) {
      return soleRecipient;
    }

    const otherMembers = this._members.filter(
      (member) => member.principalId && member.principalId !== viewerUserId,
    );

    return otherMembers.length === 1 ? otherMembers[0] : null;
  }

  public get target() {
    return this._target;
  }

  public get archivedAt() {
    return this._archived_at;
  }

  public get isArchived() {
    return this._archived_at !== null;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  /** The creation time until a message is posted. */
  public get lastMessageAt() {
    return this._last_message_at;
  }

  public get lastMessage() {
    return this._last_message;
  }

  public get unreadCount() {
    return this._unread_count;
  }

  public get muted() {
    return this._muted;
  }

  public memberFor(userId: Nullable<string>) {
    if (!userId) return null;

    return this._members.find((member) => member.principalId === userId) ?? null;
  }

  public withReadState(
    readState: Partial<{
      members: MessagingChannelMember[];
      muted: boolean;
      unreadCount: number;
    }>,
  ) {
    return this.copyWith(readState);
  }

  /** The row a message leaves behind, for a list that has not re-read it. */
  public withLastMessage(message: MessagingMessage) {
    return this.copyWith({
      lastMessageAt: message.createdAt,
      lastMessage: MessagingChannelLastMessage.fromMessage(message),
    });
  }

  public withArchivedAt(archivedAt: Dayjs) {
    return this.copyWith({ archivedAt });
  }

  private copyWith(
    overrides: Partial<{
      archivedAt: Dayjs;
      lastMessageAt: Dayjs;
      lastMessage: MessagingChannelLastMessage;
      members: MessagingChannelMember[];
      unreadCount: number;
      muted: boolean;
    }>,
  ) {
    return new MessagingChannel(
      this._id,
      this._company_id,
      this._channel_type,
      this._reply_mode,
      this._name,
      this._organizer,
      overrides.members ?? this._members,
      this._target,
      overrides.archivedAt ?? this._archived_at,
      this._created_at,
      this._updated_at,
      overrides.lastMessageAt ?? this._last_message_at,
      overrides.lastMessage ?? this._last_message,
      this._discarded_at,
      overrides.unreadCount ?? this._unread_count,
      overrides.muted ?? this._muted,
    );
  }

  constructor(
    private _id: string,
    private _company_id: string,
    private _channel_type: MessagingChannelType,
    private _reply_mode: MessagingChannelReplyMode,
    private _name: Nullable<string>,
    private _organizer: Principal_Read_Nested,
    private _members: MessagingChannelMember[],
    private _target: MessagingChannelTarget | undefined,
    private _archived_at: Nullable<Dayjs>,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _last_message_at: Dayjs,
    private _last_message: MessagingChannelLastMessage | null,
    private _discarded_at: Nullable<Dayjs>,
    private _unread_count: number,
    private _muted: boolean,
  ) {}
}

export class RepliedToBroadcastChannel {
  public static parse(proto: MessagingMessage_RepliedToBroadcastChannel_Read) {
    return new RepliedToBroadcastChannel(
      proto.id,
      proto.name,
      proto.organizer,
      proto.excerpt,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get organizer() {
    return this._organizer;
  }

  public get organizerName() {
    return principalDisplayName(this._organizer);
  }

  /**
   * Already trimmed to a single line and truncated by the api. Null whenever there is no
   * body to quote: no message yet, attachments only, or the message was deleted.
   */
  public get excerpt() {
    return this._excerpt;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    private _id: string,
    private _name: Nullable<string>,
    private _organizer: Principal_Read_Nested,
    private _excerpt: Nullable<string>,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}
}

/** A deleted message stays in the list with `deleted_at` set and `content` null. */
export class MessagingMessage {
  public static parse(proto: MessagingMessage_Read) {
    return new MessagingMessage(
      proto.id,
      proto.sender,
      proto.content ?? null,
      proto.client_message_id ?? null,
      proto.edited_at ? dayjs(proto.edited_at) : null,
      proto.deleted_at ? dayjs(proto.deleted_at) : null,
      dayjs(proto.created_at),
      proto.file_attachments.map(FileAttachmentBasic.parse),
      proto.read_count,
      proto.replied_to_broadcast_channel
        ? RepliedToBroadcastChannel.parse(proto.replied_to_broadcast_channel)
        : null,
      proto.removed_by_moderator,
    );
  }

  public get id() {
    return this._id;
  }

  public get sender() {
    return this._sender;
  }

  public get senderName() {
    return principalDisplayName(this._sender);
  }

  /** An impersonation principal carries no id, so two of them are told apart by name. */
  public get senderKey() {
    return principalId(this._sender) ?? this.senderName ?? this._id;
  }

  public get content() {
    return this._content;
  }

  /** The sender's own idempotency stamp; null on every other client's messages. */
  public get clientMessageId() {
    return this._client_message_id;
  }

  public get isDeleted() {
    return this._deleted_at !== null;
  }

  public get deletedAt() {
    return this._deleted_at;
  }

  public get removedByModerator() {
    return this._removed_by_moderator;
  }

  public get isRemovedByModerator() {
    return this.isDeleted && this._removed_by_moderator;
  }

  public get isEdited() {
    return this._edited_at !== null;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get fileAttachments() {
    return this._file_attachments;
  }

  /** Set only on a private reply to a broadcast, and quoted whether or not it was deleted. */
  public get repliedToBroadcastChannel() {
    return this._replied_to_broadcast_channel;
  }

  public get readCount() {
    return this._read_count;
  }

  /** A delete answers `204` with no body, so `deleted_at` is set locally. */
  public toTombstone({ removedByModerator }: { removedByModerator: boolean }) {
    return this.copyWith({
      content: null,
      deletedAt: dayjs(),
      removedByModerator,
      fileAttachments: [],
    });
  }

  public withReadCount(readCount: number) {
    return this.copyWith({ readCount });
  }

  private copyWith(
    overrides: Partial<{
      content: Nullable<string>;
      deletedAt: Dayjs;
      removedByModerator: boolean;
      fileAttachments: FileAttachmentBasic[];
      readCount: number;
    }>,
  ) {
    return new MessagingMessage(
      this._id,
      this._sender,
      overrides.content === undefined ? this._content : overrides.content,
      this._client_message_id,
      this._edited_at,
      overrides.deletedAt ?? this._deleted_at,
      this._created_at,
      overrides.fileAttachments ?? this._file_attachments,
      overrides.readCount ?? this._read_count,
      this._replied_to_broadcast_channel,
      overrides.removedByModerator ?? this._removed_by_moderator,
    );
  }

  constructor(
    private _id: string,
    private _sender: Principal_Read_Nested,
    private _content: Nullable<string>,
    private _client_message_id: Nullable<string>,
    private _edited_at: Nullable<Dayjs>,
    private _deleted_at: Nullable<Dayjs>,
    private _created_at: Dayjs,
    private _file_attachments: FileAttachmentBasic[],
    private _read_count: number,
    private _replied_to_broadcast_channel: Nullable<RepliedToBroadcastChannel> = null,
    private _removed_by_moderator = false,
  ) {}
}

/** Muted channels are left out of every count here. */
export class MessagingUnreadCounts {
  public static empty() {
    return new MessagingUnreadCounts(0, new Map(), new Map(), new Map(), new Map());
  }

  public static parse(proto: MessagingChannel_UnreadCounts_Read) {
    return new MessagingUnreadCounts(
      proto.total,
      new Map(
        Object.values(MessagingChannelType).map((channelType) => [
          channelType,
          proto.by_channel_type[channelType] ?? 0,
        ]),
      ),
      new Map(proto.by_order.map((order) => [order.order_id, order.unread_count])),
      new Map(proto.by_job.map((job) => [job.job_id, job.unread_count])),
      new Map(
        proto.by_channel
          .filter((channel) => !channel.muted)
          .map((channel) => [channel.messaging_channel_id, channel.unread_count]),
      ),
    );
  }

  public get total() {
    return this._total;
  }

  public forChannelType(channelType: MessagingChannelType) {
    return this._by_channel_type.get(channelType) ?? 0;
  }

  public forOrder(orderId: string) {
    return this._by_order.get(orderId) ?? 0;
  }

  public forJob(jobId: string) {
    return this._by_job.get(jobId) ?? 0;
  }

  /** Leaves a muted conversation at 0, the same as the roll-ups do. */
  public forChannel(channelId: string) {
    return this._by_channel.get(channelId) ?? 0;
  }

  constructor(
    private _total: number,
    private _by_channel_type: Map<MessagingChannelType, number>,
    private _by_order: Map<string, number>,
    private _by_job: Map<string, number>,
    private _by_channel: Map<string, number>,
  ) {}
}

export const isOwnMessage = (message: MessagingMessage, viewerUserId: Nullable<string>) =>
  Boolean(viewerUserId) && principalId(message.sender) === viewerUserId;

export class MessagingRecipient {
  constructor(
    private _id: string,
    private _first_name: string,
    private _last_name: string,
  ) {}

  public static parse(proto: MessagingChannel_Recipient_Read) {
    return new MessagingRecipient(proto.id, proto.first_name, proto.last_name);
  }

  public get id() {
    return this._id;
  }

  public get fullName() {
    return `${this._first_name} ${this._last_name}`;
  }
}
